import DismissableMessage from 'components/DismissableMessage';
import { PvCheckbox } from 'components/form/PvCheckbox';
import PvBadge from 'components/PvBadge';
import Spinner from 'components/Spinner';
import PropTypes from 'prop-types';
import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { Flex, Heading, Text, Link } from 'rebass';
import styled from 'styled-components';
import { space } from 'styled-system'
import { PvBox, PvButtonNarrow, PvUppercaseText } from 'styles/CustomComponents';
import { arrayToCsv } from 'util/FormatUtil';
import { getApplicantDriverCardDownloadUrl } from 'service/api'
import { matchCourse } from '../../util/SearchUtil';
import { includes, isEqual, pick, reject, uniqWith } from 'lodash';
import { PvCourseBadge } from '../PvBadge';
import { independentHandlingPermStatus } from './independentHandlingPermStatus';
import { personTransportPermStatus } from './personTransportPermStatus';

const BigSpan = styled.span.attrs({pl: 2})`
  font-size: 17px;
  ${space}
`;

const isTheoryLesson = (lesson) => includes(['THEORY', 'PRO_THEORY'], lesson.lessonType);
const isDrivingLesson = (lesson) => includes(['DRIVING', 'PRO_DRIVING'], lesson.lessonType);
const isTrainingLesson = (lesson) => includes(['TRAINING'], lesson.lessonType);
const isCompletedLesson = (lessonStatus) => includes(['COMPLETED', 'COMPENSATED'], lessonStatus);



const IndependentHandlingPermissionStatus = ({
  label,
  hasPermission,
  disabled,
  disabledInfo,
  updateState,
}) => {
  return (
    <Flex alignItems="center" mb={3}>
      <PvCheckbox
        title={disabledInfo}
        disabled={disabled}
        mr={2}
        type="checkbox"
        defaultChecked={hasPermission}
        onChange={(e) => {
          updateState({ independentHandlingExamPerm: e.target.checked });
        }}
      />
      {/*long text needs margin here also or some better hack*/}
      <Text ml={hasPermission ? 2 : 0}>{label}</Text>
    </Flex>
  );
};

const PersonTransportPermissionStatus = ({
  label,
  hasPermission,
  disabled,
  disabledInfo,
  updateState,
}) => {
  return (
    <Flex alignItems="center" mb={3}>
      <React.Fragment>
        <PvCheckbox
          title={disabledInfo}
          disabled={disabled}
          mr={2}
          type="checkbox"
          defaultChecked={hasPermission}
          onChange={(e) => {
            updateState({ personTransportPerm: e.target.checked });
          }}
        />
        {/*long text needs margin here also or some better hack*/}
        <Text ml={hasPermission ? 2 : 0}>{label}</Text>
      </React.Fragment>
    </Flex>
  );
};

const IndependentDrivingPermissionStatus = ({
  independentDrivingLessonsCompleted,
}) => {

  return (
    <Flex alignItems="center" mb={3}>
      <PvCheckbox
        title={I18n.t("applicant.independent_driving_lessons_disabled")}
        disabled={true}
        mr={2}
        type="checkbox"
        defaultChecked={independentDrivingLessonsCompleted}
      />
      <Translate value="applicant.independent_driving_lessons_completed" />
    </Flex>
  );
}


const ApplicantSummary = ({applicant, isTeacher, isAdmin, disableUpdateBtn, updateApplicantFn, showDriverCard, updateState, loading, error, success, mt, currentCourse}) => {
  const allApplicantLessons = (applicant.applicantLessons || []);
  const courseApplicantLessons = allApplicantLessons.filter(lesson => matchCourse(currentCourse)(lesson.course));
  const courseApplicantLessonIds = new Set(courseApplicantLessons.map(al => al.lesson.lessonId));
  const courses =  uniqWith(allApplicantLessons.filter(lesson => !!lesson.course).map(lesson => pick(pick(lesson, 'course').course, "courseNumber", "courseYear", "courseUnit")), isEqual);
  const otherCourses = reject(courses, matchCourse(currentCourse));
  const totalTheoryLessonCount = courseApplicantLessons.filter(al => !al.lesson.additionalLesson && isTheoryLesson(al.lesson)).length;
  const totalTheoryLessonsCompletedCount = applicant.lessonEntries.filter(le => isTheoryLesson(le.lesson) && isCompletedLesson(le.lessonStatus) && !le.lesson.additionalLesson && courseApplicantLessonIds.has(le.lesson.lessonId)).length;

  const requiredForDrivingCount = courseApplicantLessons.filter(al => al.lesson.requiredForIndependentTraining && !al.lesson.additionalLesson).length;
  const requiredForDrivingCompletedCount = applicant.lessonEntries
    .filter(le => le.lesson.requiredForIndependentTraining && !le.lesson.additionalLesson && isCompletedLesson(le.lessonStatus) && courseApplicantLessonIds.has(le.lesson.lessonId)).length;

  const totalDrivingLessonCount = courseApplicantLessons.filter(al => !al.lesson.additionalLesson && isDrivingLesson(al.lesson)).length;
  const totalDrivingLessonsCompletedCount = applicant.lessonEntries
    .filter(le => isDrivingLesson(le.lesson) && isCompletedLesson(le.lessonStatus) && !le.lesson.additionalLesson && courseApplicantLessonIds.has(le.lesson.lessonId)).length;

  const totalTrainingLessonCount = courseApplicantLessons.filter(al => !al.lesson.additionalLesson && isTrainingLesson(al.lesson)).length;
  const totalTrainingLessonsCompletedCount = applicant.lessonEntries
    .filter(le => isTrainingLesson(le.lesson) && isCompletedLesson(le.lessonStatus) && !le.lesson.additionalLesson && courseApplicantLessonIds.has(le.lesson.lessonId)).length;
  
  const civilDrivingLicenses =
    (() => {
      if (applicant.civilDrivingLicenses && applicant.civilDrivingLicenses.length > 0) {
        return applicant.civilDrivingLicenses.sort().reduce((one, str, index) => {
          return `${one}${index > 0 ? ', ' : ''}${str}`;
        }, '').toUpperCase();
      } else {
        return '-';
      }
    })();

  return (
    <PvBox mt={mt || 0} mb={4}>
      <PvBox borderRadius={1} py={3} px={[2, 2, 4]} bg="grays.2">
        <Heading mb={3} fontSize="19px">
          <PvUppercaseText>{applicant.lastName}</PvUppercaseText>
          <span>{` ${applicant.firstNames} (${applicant.trafiId}) `}</span>
          <PvCourseBadge course={currentCourse} />
          <PvBadge type={applicant.applicantStatus} />
        </Heading>
        <Flex flexWrap="wrap">
          <PvBox
            borderRight={["none", "none", "2px solid #ebebeb"]}
            borderBottom={["2px solid #ebebeb", "2px solid #ebebeb", "none"]}
            w={[1, 1, 1 / 2]}
            pr={[0, 0, 3]}
          >
            <Text mb={3}>
              <Translate value="applicant.theory_lessons_completed" />
              <BigSpan>{`${totalTheoryLessonsCompletedCount}/${totalTheoryLessonCount}`}</BigSpan>
            </Text>
            <Text mb={3}>
              <Translate value="applicant.training_lessons_completed" />
              <BigSpan>{`${totalTrainingLessonsCompletedCount}/${totalTrainingLessonCount}`}</BigSpan>
            </Text>
            {requiredForDrivingCount > 0 && (
              <Text mb={3}>
                <Translate value="applicant.pre_driving_lessons_completed" />
                <BigSpan>{`${requiredForDrivingCompletedCount}/${requiredForDrivingCount}`}</BigSpan>
              </Text>
            )}
            <Text mb={3}>
              <Translate value="applicant.driving_lessons_completed" />
              <BigSpan>{`${totalDrivingLessonsCompletedCount}/${totalDrivingLessonCount}`}</BigSpan>
            </Text>
            <Text mb={3}>
              <Translate value="search.other_courses" />
              {!!otherCourses &&
                otherCourses.map((course, i) => [
                  <PvCourseBadge ml={1} key={i} course={course} />,
                ])}
              {!otherCourses && (
                <BigSpan>
                  <Translate value="applicant.no_active_course" />
                </BigSpan>
              )}
            </Text>
            {/*TODO: feature toggle for admins only at this point*/}
            {
              /*isTeacher &&*/ isAdmin && (
                <Flex flexWrap="wrap">
                  {/* TODO: Data fetch broken
                <PvBox mb={3} w={[1, 1, 1, 1 / 2]} pr={3}>
                  <PvButtonNarrow w={1} height={'100%'}
                    onClick={() => showDriverCard()}>
                    <Translate value='action.open_drivercard' />
                  </PvButtonNarrow>
                </PvBox>*/}
                  <PvBox mb={3} w={[1, 1, 1, 1 / 2]} pr={3}>
                    <Link
                      href={getApplicantDriverCardDownloadUrl(
                        applicant.entityId,
                        currentCourse
                      )}
                      target="_blank"
                    >
                      <PvButtonNarrow w={1} height={"100%"}>
                        <Translate value="action.print_drivercard_pdf" />
                      </PvButtonNarrow>
                    </Link>
                  </PvBox>
                </Flex>
              )
            }
          </PvBox>
          <PvBox pl={[0, 0, 3]} pt={[3, 3, 0]} w={[1, 1, 1 / 2]}>
            <Text mb={3}>
              <Translate
                value={
                  applicant.professionalQualification
                    ? "applicant.has_pro_perm"
                    : "applicant.has_no_pro_perm"
                }
              />
            </Text>
            {!!applicant.adrPermissions && applicant.adrPermissions.length > 0 && (
              <Text mb={3}>
                <Translate value="applicant.has_adr_perm_for_classes" />
                <BigSpan>{`${arrayToCsv(applicant.adrPermissions)}`}</BigSpan>
              </Text>
            )}
            <Text mb={3}>
              <Translate value="app.civil_driving_licenses" />
              <BigSpan>{`${civilDrivingLicenses}`}</BigSpan>
            </Text>
            {applicant.proCourse && (
              <React.Fragment>
                <IndependentDrivingPermissionStatus
                  independentDrivingLessonsCompleted={
                    applicant.independentDrivingLessonsCompleted
                  }
                />
                <IndependentHandlingPermissionStatus
                  {...independentHandlingPermStatus(applicant, isTeacher)}
                  updateState={updateState}
                />
              </React.Fragment>
            )}
            {applicant.personTransportCourse && (
              <PersonTransportPermissionStatus
                {...personTransportPermStatus(applicant, isTeacher)}
                updateState={updateState}
              />
            )}
            <Flex alignItems="center" mb={3}>
              <PvCheckbox
                disabled={!isTeacher}
                mr={2}
                type="checkbox"
                defaultChecked={applicant.applicantStatus === "PASSIVE"}
                onChange={(e) => {
                  updateState({
                    applicantStatus: e.target.checked ? "PASSIVE" : "ACTIVE",
                  });
                }}
              />
              <Translate value="applicant.applicant_passive" />
            </Flex>
            {isTeacher && (
              <PvButtonNarrow
                disabled={disableUpdateBtn}
                onClick={() => updateApplicantFn(applicant.entityId)}
              >
                <Translate value="action.save_changes" />
              </PvButtonNarrow>
            )}
            {loading && <Spinner />}
            {error && !success && (
              <DismissableMessage mt={3} bg="red">
                <Translate value="message.update_applicant_error" />
              </DismissableMessage>
            )}
            {!error && success && (
              <DismissableMessage mt={3} bg="green">
                <Translate value="message.update_applicant_success" />
              </DismissableMessage>
            )}
          </PvBox>
        </Flex>
      </PvBox>
    </PvBox>
  );
};

ApplicantSummary.propTypes = {
  applicant: PropTypes.object,
  isTeacher: PropTypes.bool,
  disableUpdateBtn: PropTypes.bool,
  updateApplicantFn: PropTypes.func,
  showDriverCard: PropTypes.func,
  updateState: PropTypes.func,
  error: PropTypes.object,
  loading: PropTypes.bool,
  success: PropTypes.bool,
  currentCourse: PropTypes.object,
};

export default ApplicantSummary;





// WEBPACK FOOTER //
// ./src/components/applicant/ApplicantSummary.jsx